// Modes de modération IA d'un site + régime de configuration — dashboard Modération IA (B2_02).
//
// Deux choses distinctes vivent ici, et les confondre est le contresens que tout ce projet
// existe pour supprimer :
//   - le MODE (`modere.llm_ai_type`) = qui tranche après l'IA ;
//   - le RÉGIME (`applied` / `suggested`) = est-ce que l'IA agit elle-même, oui ou non.
// Le mode ne détermine pas le régime, et le régime ne se déduit pas du mode. Un site
// `AI_ONLY` peut très bien être en suggestion (il suffit que l'autonomie ne soit pas armée),
// et un site autonome reste en suggestion si son mode est `AI_AND_INTERNAL` (P1 D33).
//
// ─────────────────────────────────────────────────────────────────────────────
// 1. LIBELLÉS DE MODE — décision Mathias du 2026-08-27 (transverse, fenêtre B2_02)
//
// Ce sont les libellés que le CLIENT lit dans son propre back-office, repris mot pour mot.
// Le super-admin n'est pas internationalisé (aucun react-intl, aucun dossier lang/) : la
// table est donc recopiée en dur en FR, exactement comme moderationMotifs.js l'est pour la
// taxonomie des motifs (D9).
//
// ⛔ SOURCE DE RECOPIE : Loco, projet back-office — assets ETh4hp / t0hLnH / x4qVUM.
//    NE PAS les recopier depuis `graphproject/packages/back-office/public/lang/fr.json` :
//    cette copie locale est PÉRIMÉE, les trois clés n'y figurent plus (vérifié le
//    2026-08-27). Le mapping asset → mode est vérifié à la source dans
//    back-office/.../Moderation/children/AiModeration.tsx:39-41.
//
// ⚠ Pourquoi reprendre les mots du client plutôt qu'un jargon interne : le vocabulaire de
//   modération du back-office a déjà été refait une fois parce qu'il décrivait l'inverse du
//   comportement réel. Un deuxième vocabulaire, interne celui-là, rendrait incompréhensible
//   le premier échange de support qui croise les deux écrans.
const MODE_LABELS = {
  AI_ONLY: 'Verdict IA + modération IA',
  AI_AND_INTERNAL: 'Verdict IA + modération par vous',
  AI_AND_EXTERNAL: 'Externalisation de la modération chez un prestataire',
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. NORMALISATION DES MODES LEGACY (D29)
//
// ⛔ MIROIR EXACT de api/src/services/moderation.js:159-168. Toute divergence recrée la
//    contradiction entre écrans que D14 supprime.
//
// CD2 renvoie déjà `llm_ai_type` NORMALISÉ : le dashboard n'a donc rien à normaliser sur les
// données de l'endpoint. La fonction existe pour la fiche communauté (Website.js), qui lit
// `modere.llm_ai_type` BRUT depuis l'objet website — et y affiche encore `ALL_AI` tel quel.
const LEGACY_MODES = {
  ALL_AI: 'AI_ONLY',
  AI_AND_REMOTE: 'AI_AND_INTERNAL',
};
const CANONICAL_MODES = ['AI_ONLY', 'AI_AND_INTERNAL', 'AI_AND_EXTERNAL'];

function normalizeLlmAiType(value) {
  if (CANONICAL_MODES.indexOf(value) !== -1) return value;
  return LEGACY_MODES[value] || 'AI_AND_INTERNAL';
}

// Libellé client d'un mode, legacy compris. `withCode` ajoute la valeur canonique entre
// parenthèses — utile sur la fiche communauté, où la valeur brute est déjà à l'écran et où
// masquer le code ferait perdre le lien avec la donnée.
function modeLabel(value, options) {
  const canonical = normalizeLlmAiType(value);
  const label = MODE_LABELS[canonical] || canonical;
  return options && options.withCode ? `${label} (${canonical})` : label;
}


// ─────────────────────────────────────────────────────────────────────────────
// 3. RÉGIME DE CONFIGURATION (D15) — « ce que le réglage promet aujourd'hui »
//
// ⛔ MIROIR EXACT de api/src/services/moderation.js:187-188 (`shouldApplyVerdicts`), tel
//    qu'appelé par `configRegime` du contrôleur CD2 : sur le mode NORMALISÉ.
//
// ⚠ POURQUOI DUPLIQUER UNE RÈGLE SERVEUR. `shouldApplyVerdicts` n'est pas exportée vers le
//   client, et CD2 ne peut pas fournir le régime à la fiche communauté : CD2 ne renvoie que
//   les sites qui ONT CONSOMMÉ, alors que Website.js s'ouvre sur n'importe quel site, y
//   compris dormant. Brancher la fiche sur CD2 afficherait « régime inconnu » sur la
//   majorité des fiches. La règle tient en deux lignes pures — on la recopie, avec sa
//   provenance, plutôt que d'inventer un endpoint.
//
// ⚠ Sur les données de CD2, NE PAS rappeler cette fonction : l'endpoint renvoie déjà
//   `regime` (+ `regime_basis`). C'est LUI qui fait foi. Cette fonction ne sert qu'à la
//   fiche communauté, qui n'a que l'objet website sous la main.
const REGIME_APPLIED = 'applied';
const REGIME_SUGGESTED = 'suggested';

function configRegime(modere) {
  // `modere || {}` et non un défaut de paramètre : le défaut ne couvre que `undefined`,
  // alors que le serveur se garde aussi de `null` (`modere && modere.llm_ai_type`).
  const m = modere || {};
  const isApplied = normalizeLlmAiType(m.llm_ai_type) !== 'AI_AND_INTERNAL'
    && m.llm_ai_autonomous === true;
  return isApplied ? REGIME_APPLIED : REGIME_SUGGESTED;
}

// Libellés d'écran du régime.
//
// ⛔ D15 — sur l'onglet « Config par site » (CD2) et sur la fiche communauté, l'en-tête est
//    « Réglage actuel », JAMAIS « Régime » nu : c'est le réglage d'AUJOURD'HUI. Sur l'onglet
//    Suivi (CD1), la même valeur technique désigne le régime AU MOMENT DE CHAQUE DÉCISION.
//    Un même site s'affiche donc légitimement « applique » ici et majoritairement « suggère »
//    là-bas sur 90 jours — ce n'est pas une incohérence.
const REGIME_LABELS = {
  [REGIME_APPLIED]: 'applique les verdicts',
  [REGIME_SUGGESTED]: 'suggère seulement',
};

function regimeLabel(regime) {
  return REGIME_LABELS[regime] || 'régime inconnu';
}

export {
  MODE_LABELS,
  CANONICAL_MODES,
  normalizeLlmAiType,
  modeLabel,
  REGIME_APPLIED,
  REGIME_SUGGESTED,
  REGIME_LABELS,
  configRegime,
  regimeLabel,
};

export default {
  MODE_LABELS,
  CANONICAL_MODES,
  normalizeLlmAiType,
  modeLabel,
  REGIME_APPLIED,
  REGIME_SUGGESTED,
  REGIME_LABELS,
  configRegime,
  regimeLabel,
};
